import type { VoiceAgent } from '../types';

/**
 * Labels for an agent picker, disambiguated only where it is needed
 * (FLOSEAI-656).
 *
 * Two agents built from the same use-case template arrive with the same name,
 * and the trigger-call dropdown showed them as two identical rows: the user had
 * no way to know which one the call would go to.
 *
 * The obvious fixes do not work here. The reported pair shares its use case,
 * its language, its version AND its publication date — the ticket's own
 * screenshot shows both as "Primer contacto y calificación · ES (LATAM) · v1"
 * published the same day — so every human-meaningful attribute is identical.
 * The id is the only thing guaranteed to differ, which is why the
 * disambiguator is a short id rather than something prettier.
 *
 * It is added ONLY to names that actually collide. Stamping an id onto every
 * row would make the common case — a handful of differently-named agents —
 * harder to read in order to solve a case that is not present.
 */
export interface AgentOption {
  value: string;
  label: string;
}

/** Enough of a uuid to separate agents, short enough to read in a dropdown. */
export function shortAgentId(id: string): string {
  return id.slice(0, 8);
}

function collisionKey(name: string): string {
  return name.trim().toLowerCase();
}

export function buildAgentOptions(
  agents: readonly Pick<VoiceAgent, 'id' | 'name'>[],
): AgentOption[] {
  const timesSeen = new Map<string, number>();
  for (const agent of agents) {
    const key = collisionKey(agent.name);
    timesSeen.set(key, (timesSeen.get(key) ?? 0) + 1);
  }

  return agents.map((agent) => {
    const isAmbiguous = (timesSeen.get(collisionKey(agent.name)) ?? 0) > 1;
    return {
      value: agent.id,
      label: isAmbiguous ? `${agent.name} · ${shortAgentId(agent.id)}` : agent.name,
    };
  });
}
